import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { BookOpen, Heart, Brain, Sparkles, Facebook, Instagram, Linkedin, ArrowRight, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_API_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isVisible, setIsVisible] = useState({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');
    
    try {
      const response = await axios.post(`${API}/newsletter`, formData);
      
      if (response.data.success) {
        setSubmitSuccess(true);
        setFormData({ name: '', email: '' });
        setTimeout(() => setSubmitSuccess(false), 5000);
      } else {
        setErrorMessage(response.data.message);
        setTimeout(() => setErrorMessage(''), 5000);
      }
    } catch (error) {
      console.error('Error submitting newsletter:', error);
      setErrorMessage('Erro ao processar inscrição. Tente novamente.');
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: <BookOpen className="w-8 h-8" />,
      title: 'Compreensão da Vida',
      description: 'Reflexões profundas que iluminam os mistérios da existência e seu propósito maior.'
    },
    {
      icon: <Brain className="w-8 h-8" />,
      title: 'Razão e Espiritualidade',
      description: 'A união perfeita entre o pensamento racional e a sabedoria espiritual.'
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: 'Crescimento Interior',
      description: 'Ferramentas práticas para o desenvolvimento da consciência e autoconhecimento.'
    },
    {
      icon: <Sparkles className="w-8 h-8" />,
      title: 'Reflexão Transformadora',
      description: 'Mensagens diárias que inspiram mudanças reais e duradouras em sua vida.'
    }
  ];

  const reflections = [
    {
      title: 'A Força da Gratidão',
      description: 'Agradecer é reconhecer as bênçãos que nos cercam, mesmo nos momentos mais desafiadores. É através da gratidão que abrimos portas para novas conquistas.',
      author: 'Márcio Nascimento'
    },
    {
      title: 'O Poder do Presente',
      description: 'Viver o agora é a chave para a paz interior. O passado é aprendizado, o futuro é possibilidade, mas é no presente que construímos nossa realidade.',
      author: 'Márcio Nascimento'
    },
    {
      title: 'Consciência e Transformação',
      description: 'A verdadeira transformação começa quando nos tornamos conscientes de nossos pensamentos, ações e propósitos. A consciência é o primeiro passo para a evolução.',
      author: 'Márcio Nascimento'
    }
  ];

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-decoration"></div>
        <div className="container-custom">
          <div className="hero-content">
            <div className="hero-text" data-animate id="hero-text">
              <p className="hero-tagline">Reflexões Diárias para uma Vida com Propósito</p>
              <h1 className="hero-title">
                Tenha um<br />
                <span className="hero-title-highlight">Excelente Dia</span>
              </h1>
              <p className="hero-subtitle">
                Uma jornada transformadora que une razão, espiritualidade e crescimento pessoal. 
                Descubra o poder das reflexões diárias para despertar sua consciência.
              </p>
              <Button 
                className="cta-button"
                size="lg"
                onClick={() => window.open('https://www.hotmart.com', '_blank')}
              >
                Adquirir o Livro
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
            <div className="hero-image" data-animate id="hero-image">
              <div className="book-glow"></div>
              <img 
                src="https://customer-assets.emergentagent.com/job_ca033b0f-d976-450a-ab9b-4f49e23367c9/artifacts/z7y1hzqm_livro-marcio-capa.png" 
                alt="Capa do Livro Tenha um Excelente Dia"
                className="book-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About the Book */}
      <section className="section-light" data-animate id="about-book">
        <div className="container-custom">
          <div className="section-header">
            <h2 className="section-title">Sobre o Livro</h2>
            <div className="title-decoration"></div>
          </div>
          <Card className="about-card">
            <CardContent className="about-content">
              <div className="quote-mark">"</div>
              <p className="about-text">
                <strong>Tenha um Excelente Dia</strong> é mais do que um livro de reflexões diárias. 
                É um convite para despertar a consciência, unindo a racionalidade jurídica com a 
                profundidade espiritual. Cada página oferece insights transformadores que tocam 
                a alma e iluminam o caminho para uma vida plena de significado e propósito.
              </p>
              <p className="about-text-secondary">
                Escrito por um advogado e pensador espiritual, este livro apresenta uma abordagem 
                única que equilibra lógica e intuição, razão e fé, mente e coração.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-dark" data-animate id="benefits">
        <div className="container-custom">
          <div className="section-header">
            <h2 className="section-title-light">O Que Você Vai Encontrar</h2>
            <div className="title-decoration-light"></div>
          </div>
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <Card key={index} className="benefit-card">
                <CardContent className="benefit-content">
                  <div className="benefit-icon">
                    {benefit.icon}
                  </div>
                  <h3 className="benefit-title">{benefit.title}</h3>
                  <p className="benefit-description">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* About the Author */}
      <section className="section-light" data-animate id="about-author">
        <div className="container-custom">
          <div className="section-header">
            <h2 className="section-title">Sobre o Autor</h2>
            <div className="title-decoration"></div>
          </div>
          <div className="author-content">
            <div className="author-image-wrapper">
              <div className="author-glow"></div>
              <img 
                src="https://customer-assets.emergentagent.com/job_ca033b0f-d976-450a-ab9b-4f49e23367c9/artifacts/ey9d4uie_foto_marcio.jpeg" 
                alt="Márcio Nascimento"
                className="author-image"
              />
            </div>
            <div className="author-bio">
              <h3 className="author-name">Márcio Rodrigues do Nascimento</h3>
              <p className="author-description">
                Advogado com formação sólida em Direito, Márcio Nascimento trilha um caminho único 
                que une o rigor jurídico à profundidade espiritual. Estudioso do Espiritismo e 
                pensador dedicado aos mistérios da existência humana, ele oferece uma perspectiva 
                singular sobre a vida, a consciência e o propósito.
              </p>
              <p className="author-description">
                Com anos de experiência tanto no campo profissional quanto no desenvolvimento pessoal, 
                Márcio desenvolveu uma abordagem que equilibra razão e espiritualidade, oferecendo 
                reflexões que tocam tanto a mente quanto o coração.
              </p>
              <div className="author-social">
                <a href="https://www.linkedin.com/in/m%C3%A1rcio-rodrigues-do-nascimento-46207532/" target="_blank" rel="noopener noreferrer" className="social-link">
                  <Linkedin className="w-5 h-5" />
                </a>
                <a href="https://www.facebook.com/marciorodrigues.nascimento.52" target="_blank" rel="noopener noreferrer" className="social-link">
                  <Facebook className="w-5 h-5" />
                </a>
                <a href="https://www.instagram.com/marcio_nascimento_perito/" target="_blank" rel="noopener noreferrer" className="social-link">
                  <Instagram className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter / Lead Capture */}
      <section className="newsletter-section" data-animate id="newsletter">
        <div className="newsletter-decoration"></div>
        <div className="container-custom">
          <div className="newsletter-content">
            <div className="newsletter-text">
              <h2 className="newsletter-title">
                Receba Reflexões Inspiradoras
              </h2>
              <p className="newsletter-subtitle">
                Inscreva-se para receber mensagens transformadoras e fique por dentro 
                de novidades sobre o livro e conteúdos exclusivos.
              </p>
            </div>
            <form onSubmit={handleSubmit} className="newsletter-form">
              <div className="form-group">
                <Input
                  type="text"
                  placeholder="Seu nome"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="newsletter-input"
                />
                <Input
                  type="email"
                  placeholder="Seu melhor e-mail"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="newsletter-input"
                />
              </div>
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="newsletter-button"
              >
                {isSubmitting ? 'Inscrevendo...' : 'Quero Receber'}
              </Button>
              {submitSuccess && (
                <div className="success-message">
                  <CheckCircle className="w-5 h-5" />
                  <span>Inscrição realizada com sucesso!</span>
                </div>
              )}
              {errorMessage && (
                <div className="error-message">
                  <span>{errorMessage}</span>
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Reflections */}
      <section className="section-light" data-animate id="reflections">
        <div className="container-custom">
          <div className="section-header">
            <h2 className="section-title">Reflexões do Márcio</h2>
            <div className="title-decoration"></div>
            <p className="section-description">
              Pequenos fragmentos de sabedoria para iluminar o seu dia
            </p>
          </div>
          <div className="reflections-grid">
            {reflections.map((reflection, index) => (
              <Card key={index} className="reflection-card">
                <CardContent className="reflection-content">
                  <h3 className="reflection-title">{reflection.title}</h3>
                  <p className="reflection-description">{reflection.description}</p>
                  <p className="reflection-author">— {reflection.author}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Reading Invitation */}
      <section className="invitation-section" data-animate id="invitation">
        <div className="container-custom">
          <div className="invitation-content">
            <p className="invitation-text">
              "A transformação começa com um simples passo: o despertar da consciência. 
              Permita-se essa jornada."
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="cta-final-section" data-animate id="cta-final">
        <div className="container-custom">
          <div className="cta-final-content">
            <h2 className="cta-final-title">
              Comece Sua Jornada de Transformação Hoje
            </h2>
            <p className="cta-final-subtitle">
              Adquira seu exemplar e tenha um excelente dia, todos os dias.
            </p>
            <Button 
              className="cta-final-button"
              size="lg"
              onClick={() => window.open('https://www.hotmart.com', '_blank')}
            >
              Adquirir Agora
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container-custom">
          <p className="footer-text">
            © 2026 Márcio Rodrigues do Nascimento. Todos os direitos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
